/**
 * Submittals List Page
 * BuilderBel Construction Project Management System
 */

import {
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Eye,
  FileText,
  Inbox,
  Pencil,
  Plus,
  Send,
  Trash2,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { SubmittalDrawer } from '../components/submittals';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  PriorityBadge,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useDeleteSubmittalMutation,
  useGetSubmittalsQuery,
  useGetSubmittalStatsQuery,
  useSubmitForReviewMutation,
} from '../services/submittalApi';
import type {
  Submittal,
  SubmittalPriority,
  SubmittalStatus,
  SubmittalType,
} from '../types/submittal';

// ============================================================================
// Filter / Sort Options
// ============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'approved_as_noted', label: 'Approved As Noted' },
  { value: 'revise_resubmit', label: 'Revise & Resubmit' },
  { value: 'rejected', label: 'Rejected' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'shop_drawing', label: 'Shop Drawing' },
  { value: 'product_data', label: 'Product Data' },
  { value: 'sample', label: 'Sample' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'mock_up', label: 'Mock Up' },
  { value: 'test_report', label: 'Test Report' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'submittalNumber', label: 'Submittal #' },
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'title', label: 'Title' },
  { value: 'type', label: 'Type' },
];

// ============================================================================
// Helpers
// ============================================================================

const getStatusVariant = (
  status: SubmittalStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'approved_as_noted':
      return 'info';
    case 'pending_review':
    case 'under_review':
      return 'warning';
    case 'rejected':
      return 'error';
    case 'revise_resubmit':
      return 'purple';
    case 'draft':
    default:
      return 'neutral';
  }
};

const getPriorityVariant = (
  priority: SubmittalPriority
): 'low' | 'medium' | 'high' | 'critical' => {
  return priority;
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatType = (type: SubmittalType): string => {
  return type
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const extractBallInCourtName = (submittal: Submittal): string => {
  if (submittal.ballInCourtName) return submittal.ballInCourtName;
  const bic = submittal.ballInCourt;
  if (typeof bic === 'object' && bic !== null) {
    return `${bic.firstName} ${bic.lastName}`;
  }
  return '-';
};

// ============================================================================
// Page Component
// ============================================================================

const Submittals: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // Filters & pagination
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SubmittalStatus | ''>('');
  const [type, setType] = useState<SubmittalType | ''>('');
  const [priority, setPriority] = useState<SubmittalPriority | ''>('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Drawer state
  const [selectedSubmittal, setSelectedSubmittal] = useState<Submittal | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');

  // Action menu
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // API
  const {
    data: submittalsResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetSubmittalsQuery(
    {
      projectId: projectId || '',
      params: {
        page,
        limit,
        search: search || undefined,
        status: status || undefined,
        type: type || undefined,
        priority: priority || undefined,
        sortBy: sortBy as
          | 'createdAt'
          | 'dueDate'
          | 'submittalNumber'
          | 'status'
          | 'priority'
          | 'title'
          | 'type',
        sortOrder,
      },
    },
    { skip: !projectId }
  );

  const { data: statsResponse } = useGetSubmittalStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const [deleteSubmittal, { isLoading: isDeleting }] = useDeleteSubmittalMutation();
  const [submitForReview] = useSubmitForReviewMutation();

  // Extract data
  const submittals = useMemo(
    () => submittalsResponse?.data?.submittals || [],
    [submittalsResponse]
  );
  const pagination = submittalsResponse?.data?.pagination;
  const stats = statsResponse?.data;

  // Header stats
  const headerStats = useMemo(() => {
    if (!stats) return [];
    return [
      { title: 'Total', value: stats.total || 0, variant: 'brand' as const },
      {
        title: 'Pending Review',
        value: stats.pendingReview || 0,
        variant: 'warning' as const,
      },
      { title: 'Approved', value: stats.approved || 0, variant: 'success' as const },
      { title: 'Overdue', value: stats.overdue || 0, variant: 'danger' as const },
    ];
  }, [stats]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleCreate = useCallback(() => {
    setSelectedSubmittal(null);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  }, []);

  const handleView = useCallback((submittal: Submittal) => {
    setSelectedSubmittal(submittal);
    setDrawerMode('view');
    setIsDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((submittal: Submittal) => {
    setSelectedSubmittal(submittal);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedSubmittal(null);
  }, []);

  const handleDelete = useCallback(
    async (submittal: Submittal) => {
      if (!projectId) return;
      if (
        window.confirm(
          `Are you sure you want to delete ${submittal.submittalNumber}? This action cannot be undone.`
        )
      ) {
        try {
          await deleteSubmittal({
            projectId,
            submittalId: submittal._id,
          }).unwrap();
          toast.success('Submittal deleted');
        } catch (_err) {
          toast.error('Failed to delete submittal');
        }
      }
    },
    [projectId, deleteSubmittal, toast]
  );

  const handleSubmitForReview = useCallback(
    async (submittal: Submittal) => {
      if (!projectId) return;
      try {
        await submitForReview({
          projectId,
          submittalId: submittal._id,
          data: {},
        }).unwrap();
        toast.success('Submittal submitted for review');
        refetch();
      } catch (_err) {
        toast.error('Failed to submit for review');
      }
    },
    [projectId, submitForReview, toast, refetch]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // ============================================================================
  // Filters renderer
  // ============================================================================

  const renderFilters = () => (
    <div className="flex items-center gap-2">
      <SearchableDropdown
        placeholder="Status"
        value={status}
        onChange={value => {
          setStatus(value as SubmittalStatus | '');
          setPage(1);
        }}
        options={STATUS_OPTIONS}
        searchable={false}
      />
      <SearchableDropdown
        placeholder="Type"
        value={type}
        onChange={value => {
          setType(value as SubmittalType | '');
          setPage(1);
        }}
        options={TYPE_OPTIONS}
        searchable={false}
      />
      <SearchableDropdown
        placeholder="Priority"
        value={priority}
        onChange={value => {
          setPriority(value as SubmittalPriority | '');
          setPage(1);
        }}
        options={PRIORITY_OPTIONS}
        searchable={false}
      />
    </div>
  );

  // ============================================================================
  // Pagination renderer
  // ============================================================================

  const renderPagination = () => {
    if (!pagination || pagination.pages <= 1) return null;

    const { page: currentPage, pages: totalPages, total } = pagination;
    const startItem = (currentPage - 1) * limit + 1;
    const endItem = Math.min(currentPage * limit, total);

    return (
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Showing {startItem}–{endItem} of {total} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // ============================================================================
  // Row actions
  // ============================================================================

  const renderRowActions = (submittal: Submittal) => {
    const isMenuOpen = activeMenuId === submittal._id;

    const menuItems: (ActionMenuItem | 'divider')[] = [
      {
        label: 'View Details',
        icon: <Eye className="h-3.5 w-3.5" />,
        onClick: () => handleView(submittal),
      },
      {
        label: 'Edit',
        icon: <Pencil className="h-3.5 w-3.5" />,
        onClick: () => handleEdit(submittal),
        hidden: !['draft', 'revise_resubmit'].includes(submittal.status),
      },
      'divider',
      {
        label: 'Submit for Review',
        icon: <Send className="h-3.5 w-3.5" />,
        onClick: () => handleSubmitForReview(submittal),
        hidden: submittal.status !== 'draft' && submittal.status !== 'revise_resubmit',
      },
      'divider',
      {
        label: 'Delete',
        icon: <Trash2 className="h-3.5 w-3.5" />,
        onClick: () => handleDelete(submittal),
        variant: 'danger',
        disabled: isDeleting,
        hidden: !['draft', 'rejected', 'revise_resubmit'].includes(submittal.status),
      },
    ];

    return (
      <ActionMenu
        id={submittal._id}
        items={menuItems}
        isOpen={isMenuOpen}
        onOpenChange={open => setActiveMenuId(open ? submittal._id : null)}
      />
    );
  };

  // ============================================================================
  // Loading / empty project states
  // ============================================================================

  if (isLoading && submittals.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <EmptyState
          icon={Inbox}
          title="No Project Selected"
          description="Please select a project to view submittals."
        />
      </div>
    );
  }

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <ModuleLayout
      title="Submittals"
      description="Manage shop drawings, product data, samples, and review workflows"
      icon={<ClipboardList className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'New Submittal',
        onClick: handleCreate,
        icon: <Plus className="h-3 w-3 mr-1" />,
      }}
      stats={headerStats}
      searchValue={search}
      onSearchChange={value => {
        setSearch(value);
        setPage(1);
      }}
      filters={renderFilters()}
      sortOptions={SORT_OPTIONS}
      sortValue={sortBy}
      onSortChange={setSortBy}
      sortOrder={sortOrder}
      onSortOrderChange={setSortOrder}
      onRefresh={refetch}
      isLoading={isFetching}
      pagination={renderPagination()}
    >
      {submittals.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No submittals found"
          description={
            search || status || type || priority
              ? "Try adjusting your filters to find what you're looking for."
              : 'Get started by creating your first submittal.'
          }
          action={
            !search && !status && !type && !priority
              ? { label: 'Create Submittal', onClick: handleCreate }
              : undefined
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submittal #
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ball In Court
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rev #
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {submittals.map(submittal => (
                <tr
                  key={submittal._id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleView(submittal)}
                >
                  {/* Submittal # */}
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-brand-700">
                      {submittal.submittalNumber}
                    </span>
                  </td>

                  {/* Title */}
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900 truncate max-w-[220px]">
                        {submittal.title}
                      </span>
                      {submittal.specSections?.[0] && (
                        <span className="text-xs text-gray-500">
                          {submittal.specSections[0].division} — {submittal.specSections[0].section}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Type */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{formatType(submittal.type)}</span>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={submittal.status.replace(/_/g, ' ')}
                      variant={getStatusVariant(submittal.status)}
                      size="sm"
                    />
                  </td>

                  {/* Priority */}
                  <td className="px-4 py-3">
                    <PriorityBadge priority={getPriorityVariant(submittal.priority)} size="sm" />
                  </td>

                  {/* Ball in Court */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">
                      {extractBallInCourtName(submittal)}
                    </span>
                  </td>

                  {/* Due Date */}
                  <td className="px-4 py-3">
                    <span
                      className={`text-sm ${
                        submittal.isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'
                      }`}
                    >
                      {formatDate(submittal.dueDate)}
                    </span>
                  </td>

                  {/* Rev # */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 font-mono">
                      {submittal.currentRevision}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    {renderRowActions(submittal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Submittal Drawer */}
      <SubmittalDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        submittal={selectedSubmittal}
        mode={drawerMode}
        onSuccess={refetch}
      />
    </ModuleLayout>
  );
};

export default Submittals;
